import { Sparkles } from '@/components/icons';
import { Button } from '@/components/ui/button';
import type { InstrumentSuggestion } from '@/api/instrument-suggestions';

interface InstrumentSuggestionLineProps {
    suggestion: InstrumentSuggestion;
    /** Writes the suggestion into the fields it sits under. */
    onApply: () => void;
}

/**
 * What the pasted table looks like it came from, offered as a single line under
 * the field it fills.
 *
 * It proposes and never writes: the fields stay exactly as the psychologist
 * left them until the line is clicked. That is what lets the request run
 * silently — there is no half-filled state to reconcile, no "untouched field"
 * rule, and a slow answer cannot land on top of something being typed.
 *
 * It carries no dismiss control, deliberately. A line this size costs nothing
 * to ignore, and the caller withdraws it the moment the name is typed by hand
 * — the one moment where a stray click would overwrite real work.
 *
 * Nothing here takes focus, also deliberately. The dialog's paste listener
 * stands down while a text field holds the focus, so a control that grabbed it
 * would swallow the next paste.
 */
export function InstrumentSuggestionLine({ suggestion, onApply }: InstrumentSuggestionLineProps) {
    if (!suggestion.name) return null;

    // Naming the source is the point: what the click is about to write into the
    // reference is the catalog's own record, not a sentence a model wrote. A
    // `model` suggestion normally carries no citation at all.
    const referenceHint = !suggestion.reference
        ? ''
        : suggestion.source === 'satepsi'
          ? ' — com a referência do SATEPSI'
          : ' — com a referência';

    return (
        <Button
            type="button"
            variant="link"
            onClick={onApply}
            // Sized down to a line of helper text: the Button primitive is here
            // for its focus ring and its hover, not for its box.
            className="group h-auto max-w-full justify-start gap-1.5 p-0 text-[13px] font-normal leading-snug text-muted-foreground no-underline hover:no-underline [&_svg]:size-3.5"
        >
            <Sparkles variant="Bold" className="text-info" />
            <span className="min-w-0 truncate">
                Sugestão:{' '}
                <span className="font-medium text-brand underline decoration-brand/30 underline-offset-4 transition-colors group-hover:decoration-brand">
                    {suggestion.name}
                </span>
                {referenceHint}
            </span>
        </Button>
    );
}
